import React, { ComponentType, useMemo } from 'react'
import PrizeBadge from '../ScopedBlock/PrizeBadge.svg?url'
import { Tabs } from '../Tabs/Tabs'
import { Spinner, Tab } from 'react-bootstrap'
import { Modal } from '../Modal/Modal'
import { InputParametersHierarchyInfo } from '../../views/Setup/AIFeaturePresets/PresetConfig/InputParametersHierarchyInfo'
import { useInputParametersConfig } from './hooks'
import { InputParameterHierarchyType, MeetingTemplateInputParametersHierarchyConfigComponentProps } from './model'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { useGetEffectiveNodeQuery } from '../../store/ai/inputParametersHierarchy'
import { InputParametersHierarchyLevel } from '../../models/InputParametersHierarchy'

export interface MeetingTemplateInputParametersHierarchyConfigModalProps {
  show: boolean
  onHide: () => void
  type: InputParameterHierarchyType
  typeId: string
  name?: string
  meetingTemplateId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputParametersSchema: { [key: string]: any } | undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultInputParameters: { [key: string]: any } | null
  MeetingTemplateConfig: ComponentType<MeetingTemplateInputParametersHierarchyConfigComponentProps>
  CurrentUserConfig: ComponentType<MeetingTemplateInputParametersHierarchyConfigComponentProps>
  TeamMembersConfig?: ComponentType<MeetingTemplateInputParametersHierarchyConfigComponentProps>
}

export const MeetingTemplateInputParametersHierarchyConfigModal: React.FC<MeetingTemplateInputParametersHierarchyConfigModalProps> = ({
  show,
  onHide,
  type,
  typeId,
  inputParametersSchema,
  defaultInputParameters,
  name,
  meetingTemplateId,
  MeetingTemplateConfig,
  CurrentUserConfig,
  TeamMembersConfig,
}) => {
  const { ipHierarchyEnabled, hasIpHierarchyAdminScope, showTabs, teamMembersLoading } = useInputParametersConfig()

  const accountId = useAppSelector(selectAccountId)
  const { data: accountResponse } = useGetAccountQuery({ accountId })
  const lockedIPHierarchyTypes = accountResponse?.data?.enterpriseConfig?.lockedIPHierarchyTypes ?? []
  const isLockedByEnterpriseType = lockedIPHierarchyTypes.some((entry) => entry.typeId === typeId)

  const { data: meetingTemplateGlobalNode } = useGetEffectiveNodeQuery(
    {
      accountId,
      type,
      typeId,
      context: { effective: true, meetingTemplateId, level: InputParametersHierarchyLevel.MEETING_TEMPLATE_GLOBAL },
    },
    { refetchOnMountOrArgChange: true, skip: !show },
  )
  const isEnterpriseEnforced = !!meetingTemplateGlobalNode?.isEnterpriseEnforced

  const isLockedByEnterprise = isLockedByEnterpriseType || isEnterpriseEnforced

  const configProps = useMemo(() => {
    return {
      type,
      typeId,
      inputParametersSchema,
      defaultInputParameters,
      meetingTemplateId,
      onHide,
      showCustomizationSetting: !isLockedByEnterprise,
      ipHierarchyEnabled,
      lockedByEnterprise: isLockedByEnterprise,
    }
  }, [type, typeId, inputParametersSchema, defaultInputParameters, meetingTemplateId, onHide, ipHierarchyEnabled, isLockedByEnterprise])

  const userTitle = (
    <div className='d-flex align-items-center'>
      {!ipHierarchyEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Your Own
    </div>
  )

  const teamMembersTitle = (
    <div className='d-flex align-items-center'>
      {!ipHierarchyEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Your Team Members
    </div>
  )

  const enterpriseLockedInputParametersInfo = isLockedByEnterprise ? (
    <InputParametersHierarchyInfo icon='bi-exclamation-square' message='Preset configurations are managed at the enterprise level.' />
  ) : null

  const tabConfig = (
    <Tabs defaultActiveKey={hasIpHierarchyAdminScope ? 'global' : 'currentUser'} id='preset-config-tabs' className='d-flex mb-3' mountOnEnter unmountOnExit>
      <Tab eventKey='global' title='Meeting Type Level'>
        {isLockedByEnterprise ? null : (
          <InputParametersHierarchyInfo
            icon='bi-exclamation-square'
            message='These settings apply to everyone, unless they have their own settings for meeting type.'
          />
        )}
        {enterpriseLockedInputParametersInfo}
        <MeetingTemplateConfig {...configProps} />
      </Tab>
      <Tab eventKey='currentUser' title={userTitle}>
        {isLockedByEnterprise ? null : (
          <InputParametersHierarchyInfo icon='bi-info-square' message='These settings will only apply to you for meeting type. ' />
        )}
        {enterpriseLockedInputParametersInfo}
        <CurrentUserConfig {...configProps} />
      </Tab>
      {TeamMembersConfig && (
        <Tab eventKey='teamMembers' title={teamMembersTitle}>
          {isLockedByEnterprise ? null : (
            <InputParametersHierarchyInfo icon='bi-info-square' message='Manage settings for your team members for meeting type. ' />
          )}
          {enterpriseLockedInputParametersInfo}
          <TeamMembersConfig {...configProps} />
        </Tab>
      )}
    </Tabs>
  )

  const singleConfig = ipHierarchyEnabled ? <CurrentUserConfig {...configProps} /> : <MeetingTemplateConfig {...configProps} />

  const body = (
    <div className='form-disabled-config'>
      <h4 className='text-center mb-5'>{`Customize ${name} for meeting type`}</h4>
      {teamMembersLoading ? (
        <div className='d-flex align-items-center justify-content-center p-2'>
          <Spinner size={'sm'} />
        </div>
      ) : showTabs ? (
        tabConfig
      ) : (
        <>
          {enterpriseLockedInputParametersInfo}
          {singleConfig}
        </>
      )}
    </div>
  )

  return <Modal size='lg' show={show} onHide={onHide} header={<></>} body={body} footer={<></>} />
}
